import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// RevenueCat API keys from dashboard
const REVENUECAT_API_KEY_IOS = 'appl_PlGgsIexyvrlovCKHqgQLpyOAvQ';
const REVENUECAT_API_KEY_ANDROID = 'appl_PlGgsIexyvrlovCKHqgQLpyOAvQ';

const SubscriptionProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [offerings, setOfferings] = useState(null);
  const [error, setError] = useState(null);
  const [isNativePlatform, setIsNativePlatform] = useState(false);

  const updateSubscriptionState = useCallback((info) => {
    setCustomerInfo(info);
    const hasActiveSubscription =
      info?.entitlements?.active && Object.keys(info.entitlements.active).length > 0;
    setIsSubscribed(hasActiveSubscription);
  }, []);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!isNativePlatform) return;

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      updateSubscriptionState(customerInfo);
    } catch (err) {
      console.error('Error checking subscription:', err);
      setError(err);
    }
  }, [isNativePlatform, updateSubscriptionState]);

  // Initialize RevenueCat
  useEffect(() => {
    const initRevenueCat = async () => {
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();
      setIsNativePlatform(isNative);

      if (!isNative) {
        setIsLoading(false);
        return;
      }

      try {
        const apiKey = platform === 'ios'
          ? REVENUECAT_API_KEY_IOS
          : REVENUECAT_API_KEY_ANDROID;

        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
        await Purchases.configure({ apiKey });

        const { customerInfo } = await Purchases.getCustomerInfo();
        updateSubscriptionState(customerInfo);

        await Purchases.addCustomerInfoUpdateListener((info) => {
          updateSubscriptionState(info.customerInfo);
        });
      } catch (err) {
        console.error('RevenueCat init error:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    initRevenueCat();
  }, [updateSubscriptionState]);

  const getOfferings = useCallback(async () => {
    if (!isNativePlatform) {
      return null;
    }

    try {
      const offerings = await Purchases.getOfferings();
      setOfferings(offerings);
      return offerings;
    } catch (err) {
      console.error('Error getting offerings:', err);
      setError(err);
      return null;
    }
  }, [isNativePlatform]);

  const purchasePackage = useCallback(async (pkg) => {
    if (!isNativePlatform) {
      return { success: false, error: new Error('Purchases not available on web') };
    }

    try {
      setIsLoading(true);
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      updateSubscriptionState(customerInfo);
      return { success: true };
    } catch (err) {
      if (err.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        return { success: false, cancelled: true };
      }
      console.error('Purchase error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [isNativePlatform, updateSubscriptionState]);

  const restorePurchases = useCallback(async () => {
    if (!isNativePlatform) {
      return { success: false, error: new Error('Purchases not available on web') };
    }

    try {
      setIsLoading(true);
      const { customerInfo } = await Purchases.restorePurchases();
      updateSubscriptionState(customerInfo);
      return { success: true, hasSubscription: Object.keys(customerInfo?.entitlements?.active || {}).length > 0 };
    } catch (err) {
      console.error('Restore error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [isNativePlatform, updateSubscriptionState]);

  const value = {
    isLoading,
    isSubscribed,
    customerInfo,
    offerings,
    error,
    isNativePlatform,
    getOfferings,
    purchasePackage,
    restorePurchases,
    checkSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
